/**
 * PATH: apps/qa/src/pages/SignoffsPage.tsx
 * PURPOSE: Display and manage QA hold points and sign-off requests
 * SUB-MODULES: ./signoffs-page/
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@quotech/auth'
import api from '@/lib/api'
import { cn, formatDateTime } from '@/lib/utils'
import {
  CheckCircle, Clock, AlertTriangle, XCircle, Search, Loader2,
  User, Calendar, MapPin, FileText, RefreshCw,
} from 'lucide-react'

import type { HoldPoint, SignerDetails } from './signoffs-page/types'
import { ApprovalModal } from './signoffs-page/ApprovalModal'
import { RejectionModal } from './signoffs-page/RejectionModal'

export default function SignoffsPage() {
  const queryClient = useQueryClient()
  const [searchQuery, setSearchQuery] = useState('')
  const [filterStatus, setFilterStatus] = useState<'all' | HoldPoint['status']>('all')
  const [approvalModal, setApprovalModal] = useState<HoldPoint | null>(null)
  const [rejectionModal, setRejectionModal] = useState<HoldPoint | null>(null)

  const organisationId = useAuthStore((s) => s.organisationId)
  const isAuthReady = !!organisationId

  const { data: holdPointsData, isLoading, error, refetch } = useQuery({
    queryKey: ['qa-hold-points', filterStatus, organisationId],
    queryFn: async () => {
      const params: Record<string, string> = {}
      if (filterStatus !== 'all') params.status = filterStatus
      return (await api().get('/qa/hold-points/', { params })).data
    },
    enabled: isAuthReady,
  })

  const showLoading = isLoading || !isAuthReady
  const holdPoints: HoldPoint[] = holdPointsData?.results || []

  const approveMutation = useMutation({
    mutationFn: async ({ holdPointId, signatureAssetId, signerDetails }: { holdPointId: string; signatureAssetId: string; signerDetails: SignerDetails }) => {
      return (await api().post(`/qa/hold-points/${holdPointId}/approve/`, { signature: { signer_name: signerDetails.signer_name, signer_role: signerDetails.signer_role, signer_organisation: signerDetails.signer_company, signature_asset_id: signatureAssetId } })).data
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['qa-hold-points'] }); setApprovalModal(null) },
  })

  const rejectMutation = useMutation({
    mutationFn: async ({ holdPointId, reason }: { holdPointId: string; reason: string }) => {
      return (await api().post(`/qa/hold-points/${holdPointId}/reject/`, { comment: reason })).data
    },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['qa-hold-points'] }); setRejectionModal(null) },
  })

  const filteredHoldPoints = holdPoints.filter(hp =>
    hp.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    hp.inspection_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    hp.area_name?.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const pendingCount = holdPoints.filter(hp => hp.status === 'requested').length
  const approvedCount = holdPoints.filter(hp => hp.status === 'approved').length
  const rejectedCount = holdPoints.filter(hp => hp.status === 'rejected').length

  const getStatusConfig = (status: string, isCritical: boolean) => {
    if (status === 'approved') return { icon: <CheckCircle className="w-5 h-5" />, bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-400', label: 'Approved' }
    if (status === 'rejected') return { icon: <XCircle className="w-5 h-5" />, bg: 'bg-destructive/10', text: 'text-destructive', label: 'Rejected' }
    if (isCritical) return { icon: <AlertTriangle className="w-5 h-5" />, bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-400', label: 'Critical - Requested' }
    return { icon: <Clock className="w-5 h-5" />, bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-400', label: 'Requested' }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div><h1 className="text-2xl font-bold text-foreground">Sign-offs</h1><p className="text-muted-foreground">Hold points and approvals across inspections</p></div>
        <button onClick={() => refetch()} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-input text-foreground font-medium hover:bg-accent transition-colors"><RefreshCw className="w-4 h-4" />Refresh</button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { status: 'requested' as const, count: pendingCount, label: 'Pending Approval', icon: Clock, color: 'amber', borderColor: 'border-amber-500 bg-amber-50 dark:bg-amber-900/20' },
          { status: 'approved' as const, count: approvedCount, label: 'Approved', icon: CheckCircle, color: 'emerald', borderColor: 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' },
          { status: 'rejected' as const, count: rejectedCount, label: 'Rejected', icon: XCircle, color: 'destructive', borderColor: 'border-destructive bg-destructive/10' },
        ].map(({ status, count, label, icon: Icon, color, borderColor }) => (
          <button key={status} onClick={() => setFilterStatus(status)} className={cn('p-4 rounded-xl border text-left transition-all', filterStatus === status ? borderColor : 'border-border bg-card hover:bg-accent')}>
            <div className="flex items-center gap-3">
              <div className={cn("p-2 rounded-lg", `bg-${color}-100 dark:bg-${color}-900/30`)}><Icon className={cn("w-5 h-5", `text-${color}-600 dark:text-${color}-400`)} /></div>
              <div><p className="text-2xl font-bold text-foreground">{count}</p><p className="text-sm text-muted-foreground">{label}</p></div>
            </div>
          </button>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" /><input type="text" placeholder="Search hold points..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary" /></div>
        <button onClick={() => setFilterStatus('all')} className={cn('px-4 py-2.5 rounded-xl border font-medium transition-colors', filterStatus === 'all' ? 'border-primary bg-primary/10 text-primary' : 'border-input text-muted-foreground hover:bg-accent')}>Show All</button>
      </div>

      {/* Hold Points List */}
      {showLoading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>
      ) : error ? (
        <div className="text-center py-16"><div className="w-16 h-16 rounded-2xl bg-destructive/10 mx-auto mb-4 flex items-center justify-center"><AlertTriangle className="w-8 h-8 text-destructive" /></div><p className="text-foreground font-medium">Failed to load sign-offs</p></div>
      ) : filteredHoldPoints.length === 0 ? (
        <div className="text-center py-16"><div className="w-16 h-16 rounded-2xl bg-muted mx-auto mb-4 flex items-center justify-center"><CheckCircle className="w-8 h-8 text-muted-foreground" /></div><p className="text-foreground font-medium">No sign-offs found</p><p className="text-sm text-muted-foreground mt-1">{filterStatus !== 'all' ? `No ${filterStatus} hold points found. Try showing all.` : 'Hold points will appear here when added to inspections.'}</p></div>
      ) : (
        <div className="space-y-3">
          {filteredHoldPoints.map((holdPoint) => {
            const isCritical = Array.isArray(holdPoint.required_signer_roles) && holdPoint.required_signer_roles.length > 0
            const sc = getStatusConfig(holdPoint.status, isCritical)
            return (
              <div key={holdPoint.id} className="bg-card rounded-xl shadow-sm border border-border p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start gap-4">
                  <div className={cn('p-2 rounded-lg', sc.bg)}><span className={sc.text}>{sc.icon}</span></div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <h3 className="font-semibold text-foreground flex items-center gap-2">{holdPoint.name}{isCritical && <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">Critical</span>}</h3>
                      <span className={cn('inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full font-medium flex-shrink-0', sc.bg, sc.text)}>{sc.label}</span>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                      {holdPoint.inspection_name && <Link to={`/records/${holdPoint.inspection_id}`} className="flex items-center gap-1 hover:text-primary transition-colors"><FileText className="w-3.5 h-3.5" />{holdPoint.inspection_name}</Link>}
                      {holdPoint.area_name && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{holdPoint.floor_name && `${holdPoint.floor_name} › `}{holdPoint.area_name}</span>}
                      {holdPoint.requested_by_name && <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" />{holdPoint.requested_by_name}</span>}
                      {holdPoint.requested_at && <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{formatDateTime(holdPoint.requested_at)}</span>}
                    </div>
                    {holdPoint.status === 'approved' && holdPoint.approved_by_name && <div className="mt-2 p-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 text-sm"><span className="text-emerald-700 dark:text-emerald-400">Approved by {holdPoint.approved_by_name}{holdPoint.approved_at && ` on ${formatDateTime(holdPoint.approved_at)}`}</span></div>}
                    {holdPoint.status === 'rejected' && <div className="mt-2 p-2 rounded-lg bg-destructive/10 text-sm"><span className="text-destructive">Rejected by {holdPoint.rejected_by_name || 'Unknown'}{holdPoint.rejected_at && ` on ${formatDateTime(holdPoint.rejected_at)}`}</span>{holdPoint.decision_comment && <p className="text-destructive/80 mt-1">{holdPoint.decision_comment}</p>}</div>}
                  </div>
                  {holdPoint.status === 'requested' && (
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button onClick={() => setApprovalModal(holdPoint)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500 text-white text-sm font-medium hover:bg-emerald-600 transition-colors"><CheckCircle className="w-4 h-4" />Approve</button>
                      <button onClick={() => setRejectionModal(holdPoint)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-destructive text-destructive text-sm font-medium hover:bg-destructive/10 transition-colors"><XCircle className="w-4 h-4" />Reject</button>
                    </div>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {approvalModal && <ApprovalModal holdPoint={approvalModal} onClose={() => setApprovalModal(null)} onApprove={(sig, det) => approveMutation.mutate({ holdPointId: approvalModal.id, signatureAssetId: sig, signerDetails: det })} isSubmitting={approveMutation.isPending} />}
      {rejectionModal && <RejectionModal holdPoint={rejectionModal} onClose={() => setRejectionModal(null)} onReject={(reason) => rejectMutation.mutate({ holdPointId: rejectionModal.id, reason })} isSubmitting={rejectMutation.isPending} />}
    </div>
  )
}
